// "In danger" ring: a dashed UNESCO-blue circle on the ground of a hex whose World Heritage site is
// on the UNESCO danger list at the date. A surface layer (one flag texture, no extra geometry), so
// it follows the relief and needs no transparency. Blue and dashed, so it never reads as a border,
// a front line or a fire; the chip's blue rim and its hover text say what it is.
//
//   danger flags per hex ─► R8 texture ─► layer: dashed ring at RING_RADIUS ─► blue albedo + a faint glow
//
// The ring fades out with camera height (the chip's blue rim takes over) and never draws on water:
// the ocean is its own mesh.

import * as THREE from 'three/webgpu';
import { atan, float, fract, length, max, mix, smoothstep, textureLoad } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { COLS, HEX_COUNT, ROWS } from '../../core/grid.ts';
import type { SurfaceCtx, SurfaceLayer } from '../surface.ts';
import { rgb } from '../tsl/color.ts';
import { cellCoord } from '../tsl/hex.ts';

type F = Node<'float'>;

const LAYER_NAME = 'sites-danger';
const LAYER_ORDER = 120; // after lakes (100) and rivers (110), before the highlight (900)
const BLUE = '#2d8fe0'; // UNESCO's blue
const RING_RADIUS = 0.8;
const RING_WIDTH = 0.04;
const RING_MIN_PX = 1.4; // pixels: the ring never gets thinner than this on screen
const RING_SOFT = 0.5; // share of the width that is edge falloff
const DASHES = 18; // a whole number, so the seam of the angle falls between two dashes
const DASH_SHARE = 0.6; // of each dash period that is drawn
const DASH_SOFT = 0.1; // of a period: the falloff at each dash end
const FADE_FROM = 55; // camera height (hex radii)
const FADE_TO = 110;
const ALBEDO_MIX = 0.8;
const GLOW = 0.3;
const FLAG = 255;

export class DangerRing {
  readonly layer: SurfaceLayer;
  private readonly flags = new THREE.DataTexture(new Uint8Array(HEX_COUNT), COLS, ROWS, THREE.RedFormat, THREE.UnsignedByteType);

  constructor() {
    const tex = this.flags;
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;

    // Ring mask: 0..1, zero away from the ring and between dashes, on flagged hexes, near the ground.
    const ring = (ctx: SurfaceCtx): F => {
      const flag = textureLoad(tex, cellCoord(ctx.hex)).x;
      const width = max(float(RING_WIDTH), ctx.px.mul(RING_MIN_PX));
      const d = length(ctx.hex.local).sub(RING_RADIUS).abs();
      const line = float(1).sub(smoothstep(width.mul(RING_SOFT), width, d));
      const near = float(1).sub(smoothstep(FADE_FROM, FADE_TO, ctx.camHeight));
      const turn = atan(ctx.hex.local.y, ctx.hex.local.x).div(Math.PI * 2).add(0.5);
      const phase = fract(turn.mul(DASHES));
      const dash = smoothstep(0, DASH_SOFT, phase).mul(float(1).sub(smoothstep(DASH_SHARE, DASH_SHARE + DASH_SOFT, phase)));
      return line.mul(dash).mul(flag).mul(near);
    };

    this.layer = {
      name: LAYER_NAME,
      order: LAYER_ORDER,
      color(ctx) {
        return mix(ctx.color, rgb(BLUE), ring(ctx).mul(ALBEDO_MIX));
      },
      emissive(ctx) {
        return rgb(BLUE).mul(ring(ctx).mul(GLOW));
      },
    };
  }

  /** Flag exactly these hexes. */
  setHexes(hexes: Iterable<number>): void {
    const data = this.flags.image.data as Uint8Array;
    data.fill(0);
    for (const hex of hexes) {
      data[hex] = FLAG;
    }
    this.flags.needsUpdate = true;
  }

  dispose(): void {
    this.flags.dispose();
  }
}
